import React, { useState, useCallback } from "react";
import { Modal, Button, Flex, Text, toast, FileUpload, FileObjectType } from "@jsq/ds/core";
import { type ExtensionFieldObjectType } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { useBulkUpload } from "../../hooks/useBulkUpload";
import { generateCsvTemplate, createAndDownloadFile } from "../../utils/utils";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export type UploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  extensionFields: ExtensionFieldObjectType[];
  onUploadSuccess?: () => void;
};

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  extensionFields,
  onUploadSuccess,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileObjectType[]>([]);

  const { uploadFileAndQueueTask, isUploading } = useBulkUpload({
    onUploadSuccess,
    onClose: handleClose,
  });

  const handleDownloadTemplate = useCallback(() => {
    const csvContent = generateCsvTemplate(extensionFields);
    createAndDownloadFile(
      csvContent,
      "extension_field_bulk_upload_template.csv",
      "text/csv;charset=utf-8;",
    );
    toast.success("Template downloaded successfully");
  }, [extensionFields]);

  const handleFilesSelected = useCallback((files: FileObjectType[]) => {
    const acceptedFiles = files.filter((file) => file.status === "accepted");
    setSelectedFiles(acceptedFiles);
  }, []);

  const handleUploadClick = useCallback(async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select a file to upload");
      return;
    }

    const file = selectedFiles[0].file;
    await uploadFileAndQueueTask(file);
  }, [selectedFiles, uploadFileAndQueueTask]);

  function handleClose() {
    setSelectedFiles([]);
    onClose();
  }

  return (
    <Modal show={isOpen} onHide={handleClose} size="medium" data-testid="upload-modal">
      <Modal.Header closeButton>
        <Text variant="h3">Bulk upload extension field values</Text>
      </Modal.Header>
      <Modal.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <Flex flexDirection="column" gap={SPACING.MEDIUM}>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text variant="strong-md">Step 1: Download template</Text>
              <Text variant="regular-sm">
                Download the CSV template with the correct column headers (arena_id,
                entity_name, and all extension fields).
              </Text>
            </Flex>
            <Button
              variant="neutral"
              icon="download"
              onClick={handleDownloadTemplate}
              data-testid="download-template-btn"
            >
              Download template
            </Button>
          </Flex>

          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text variant="strong-md">Step 2: Upload file</Text>
              <Text variant="regular-sm">
                Select your filled CSV file (must be less than 10 MB).
              </Text>
            </Flex>
            <Flex justifyContent="center">
              <FileUpload
                onFileSelected={handleFilesSelected}
                acceptedFileTypes={[".csv"]}
                maxFileSize={MAX_FILE_SIZE}
                data-testid="file-upload"
              />
            </Flex>
          </Flex>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button
            variant="neutral"
            onClick={handleClose}
            disabled={isUploading}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadClick}
            isLoading={isUploading}
            disabled={selectedFiles.length === 0 || isUploading}
            data-testid="upload-submit-btn"
          >
            Upload and process
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
